import axios from 'axios';

const BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const api = axios.create({ baseURL: BASE });

api.interceptors.request.use((cfg) => {
  const isAdmin = cfg.url && cfg.url.includes('/admin/');
  const token = isAdmin ? localStorage.getItem('cs_admin_token') : localStorage.getItem('cs_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

let _reloading = false;
const forceReloadOnce = () => {
  if (_reloading) return;
  _reloading = true;
  // Give any in-flight requests a moment, then reload to force re-login
  setTimeout(() => { try { window.location.reload(); } catch (e) { /* noop */ } }, 300);
};

api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || '';
    const isAdmin = url.includes('/admin/');
    // Never force-logout on login/register attempts (401 there is expected)
    const isAuthAttempt = url.includes('/auth/login') || url.includes('/auth/register') || url.endsWith('/admin/login');

    if (status === 401 && !isAuthAttempt) {
      if (isAdmin) {
        localStorage.removeItem('cs_admin_token');
      } else {
        localStorage.removeItem('cs_token');
      }
      forceReloadOnce();
    }
    return Promise.reject(err);
  }
);

export const authApi = {
  register: (data) => api.post('/auth/register', data).then((r) => r.data),
  login: (data) => api.post('/auth/login', data).then((r) => r.data),
  me: () => api.get('/auth/me').then((r) => r.data),
  adminLogin: (data) => api.post('/admin/login', data).then((r) => r.data),
};

export const productsApi = {
  list: () => api.get('/products').then((r) => r.data),
};

export const walletApi = {
  get: () => api.get('/wallet').then((r) => r.data),
  deposit: (data) => api.post('/wallet/deposit', data).then((r) => r.data),
};

export const ordersApi = {
  list: () => api.get('/orders').then((r) => r.data),
  create: (data) => api.post('/orders', data).then((r) => r.data),
};

export const paymentsApi = {
  list: () => api.get('/payment-methods').then((r) => r.data),
};

export const settingsApi = {
  get: () => api.get('/settings').then((r) => r.data),
};

export const adminApi = {
  // products
  listProducts: () => api.get('/admin/products').then((r) => r.data),
  createProduct: (d) => api.post('/admin/products', d).then((r) => r.data),
  updateProduct: (id, d) => api.patch(`/admin/products/${id}`, d).then((r) => r.data),
  deleteProduct: (id) => api.delete(`/admin/products/${id}`).then((r) => r.data),
  addKeys: (id, keys) => api.post(`/admin/products/${id}/keys`, { keys }).then((r) => r.data),
  listKeys: (id) => api.get(`/admin/products/${id}/keys`).then((r) => r.data),
  // deposits
  listDeposits: (status) => api.get('/admin/deposits', { params: { status } }).then((r) => r.data),
  approveDeposit: (id) => api.post(`/admin/deposits/${id}/approve`).then((r) => r.data),
  rejectDeposit: (id, reason) => api.post(`/admin/deposits/${id}/reject`, { reason }).then((r) => r.data),
  // orders
  listOrders: (status) => api.get('/admin/orders', { params: { status } }).then((r) => r.data),
  deliverOrder: (id, credential) => api.post(`/admin/orders/${id}/deliver`, { credential }).then((r) => r.data),
  refundOrder: (id) => api.post(`/admin/orders/${id}/refund`).then((r) => r.data),
  // payment methods
  listPaymentMethods: () => api.get('/admin/payment-methods').then((r) => r.data),
  createPaymentMethod: (d) => api.post('/admin/payment-methods', d).then((r) => r.data),
  updatePaymentMethod: (id, d) => api.patch(`/admin/payment-methods/${id}`, d).then((r) => r.data),
  deletePaymentMethod: (id) => api.delete(`/admin/payment-methods/${id}`).then((r) => r.data),
  // users
  listUsers: () => api.get('/admin/users').then((r) => r.data),
  updateUser: (id, d) => api.patch(`/admin/users/${id}`, d).then((r) => r.data),
  // stats
  stats: () => api.get('/admin/stats').then((r) => r.data),
  // settings
  getSettings: () => api.get('/admin/settings').then((r) => r.data),
  updateSettings: (d) => api.patch('/admin/settings', d).then((r) => r.data),
  // support
  listThreads: () => api.get('/admin/support/threads').then((r) => r.data),
  getThread: (userId) => api.get(`/admin/support/messages/${userId}`).then((r) => r.data),
  replyThread: (userId, text) => api.post(`/admin/support/reply/${userId}`, { text }).then((r) => r.data),
  supportUnread: () => api.get('/admin/support/unread').then((r) => r.data),
};

export default api;
